import { createSlice } from '@reduxjs/toolkit';
import { getStatusMessage } from '../../utils/response-code-messages';
import Snackbar from '../../common/snackBar';
import get from 'lodash/get';
import {
  AssetHistoryActionTypes,
  AssetsLengthRequestActionTypes,
  AssetsOnboardingDataRequestActionTypes,
  AssetsRequestActionTypes,
  BackendServerLengthRequestActionTypes,
  GatewaysLatestRequestActionTypes,
  GatewaysLengthRequestActionTypes,
  InfluxLengthRequestActionTypes,
  MQBrokerLengthRequestActionTypes,
  MQBrokerRequestActionTypes,
  NotificationsRequestActionTypes,
  TelemtryIngestionDataRequestActionTypes,
  UIServerLengthRequestActionTypes,
} from '../models/connectivity-dashboard';

const page_name = 'conDashboard';

const initialState: any = {
  isDataLoading: false,
  conDashData: [],
  isDataHaveError: false,
  message: '',

  // Assets
  // assetsLength: { connected_assets_count: 0, disconnected_assets_count: 0 },
  // assetsLengthLoading: false,
  // assetsLengthRequestFailed: false,

  assetsCount: { connected: 0, disconnected: 0 },
  assetsLatest: [],
  assetsLatestLoading: true,
  assetsLatestFailed: false,
  assetsLatestTimestamp: null,

  assets: [],
  assetsLoading: true,
  assetsRequestFailed: false,
  assetsTotalCount: 0,
  assetsPageNumber: 1,
  assetsTimestamp: null,

  assetsOnboardingData: {},
  assetsOnboardingLoading: false,
  assetsOnboardingRequestFailed: false,

  // Gateways
  // gatewaysLength: { connected_gateways_count: 0, disconnected_gateways_count: 0 },
  // gatewaysLengthLoading: false,
  // gatewaysLengthRequestFailed: false,

  gatewaysCount: { connected: 0, disconnected: 0 },
  gatewaysLatest: [],
  gatewaysLatestLoading: true,
  gatewaysLatestFailed: false,
  gatewaysLatestTimestamp: null,

  gateways: [],
  gatewaysLoading: true,
  gatewaysRequestFailed: false,
  gatewaysTotalCount: 0,
  gatewaysPageNumber: 1,
  gatewaysTimestamp: null,

  // MQ Broker
  mqBrokerLength: { connected_emqx_count: 0, disconnected_emqx_count: 0 },
  mqBrokerLengthLoading: false,
  mqBrokerLengthRequestFailed: false,

  mqBroker: [],
  mqBrokerLoading: false,
  mqBrokerRequestFailed: false,
  mqBrokerTotalCount: 0,
  mqBrokerPageNumber: 1,

  // Influx
  influxLength: { connected_influx_count: 0, disconnected_influx_count: 0 },
  influxLengthLoading: false,
  influxLengthRequestFailed: false,

  influx: [],
  influxLoading: false,
  influxRequestFailed: false,
  influxTotalCount: 0,
  influxPageNumber: 1,

  // UI Server
  uiServerLength: { connected_ui_count: 0, disconnected_ui_count: 0 },
  uiServerLengthLoading: false,
  uiServerLengthRequestFailed: false,

  uiServer: [],
  uiServerLoading: false,
  uiServerRequestFailed: false,
  uiServerTotalCount: 0,
  uiServerPageNumber: 1,

  // Backend Server
  backendServerLength: { connected_backend_count: 0, disconnected_backend_count: 0 },
  backendServerLengthLoading: false,
  backendServerLengthRequestFailed: false,

  backendServer: [],
  backendServerLoading: false,
  backendServerRequestFailed: false,
  backendServerTotalCount: 0,
  backendServerPageNumber: 1,

  // Notifications
  notificationsLatest: [],
  notificationsLatestLoading: false,
  notificationsLatestFailed: false,

  notifications: [],
  notificationsLoading: false,
  notificationsRequestFailed: false,
  notificationsTotalCount: 0,
  notificationsPageNumber: 1,

  // Telemetry
  telemetryIngestionData: [],
  telemetryIngestionLoading: false,
  telemetryIngestionRequestFailed: false,

  //Assets History
  assetsHistoryData: [],
  assetsHistoryLoading: false,
  assetsHistoryRequestFailed: false,
  assetHistoryTotalCount: 0,
  assetHistoryPageNumber: 1,
  assetsHistoryTimestamp: null,

  // EMQX - Influx Count
  emqxInfluxCount: {
    emqx_count: 0,
    connected_emqx_count: 0,
    disconnected_emqx_count: 0,
    influx_count: 0,
    connected_influx_count: 0,
    disconnected_influx_count: 0,
  },
  emqxInfluxCountLoading: true,
  emqxInfluxCountFailed: false,

  assetPropertiesData: [],
  assetsPropertiesLoading: false,
  assetsPropertiesRequestFailed: false,
  assetPropertiesTotalCount: 0,
  assetPropertiesPageNumber: 1,
  assetsPropertiesTimestamp: null,
};

const connectivityDashboardReducer = createSlice({
  name: page_name,
  initialState: initialState,
  reducers: {
    getConDashboardDataRequest: (state, action: { payload: string }) => {
      state.isDataLoading = true;
    },
    getConDashboardDataResponse: (state, action) => {
      state.isDataLoading = false;
      state.conDashData = action.payload.res;
    },
    getConDashboardDataFailed: (state, action) => {
      state.isDataLoading = false;
      state.isDataHaveError = true;
      const message = getStatusMessage(page_name, action.payload);
      state.componentMessage = message;
      Snackbar.error(message);
    },

    // Assets
    getAssetsLengthRequest: (state, actions: AssetsLengthRequestActionTypes) => {
      state.assetsLengthLoading = true;
      state.assetsLengthRequestFailed = false;
    },
    getAssetsLengthResponse: (state, action) => {
      state.assetsLengthLoading = false;
      state.assetsLength = get(action, 'payload.res', {});
    },
    getAssetsLengthFailed: (state, action) => {
      state.assetsLengthLoading = false;
      state.assetsLengthRequestFailed = true;
      const message = getStatusMessage(page_name, action.payload);
      state.componentMessage = message;
      Snackbar.error(message);
    },

    // Assets Latest
    getAssetsLatestRequest: (state, action: GatewaysLatestRequestActionTypes) => {
      state.assetsLatestLoading = true;
      state.assetsLatestFailed = false;
      state.assetsLatestTimestamp = action.payload?.timestamp;
    },
    getAssetsLatestResponse: (state, action) => {
      if (state.assetsLatestTimestamp === get(action, 'payload.res.timestamp', null)) {
        state.assetsLatestLoading = false;
        state.assetsLatest = get(action, 'payload.res.data', []);
        state.assetsCount = {
          connected: get(action, 'payload.res.connected_count', 0),
          disconnected: get(action, 'payload.res.disconnected_count', 0),
        };
      }
    },
    getAssetsLatestFailed: (state, action) => {
      state.assetsLatestLoading = false;
      state.assetsLatestFailed = true;
      const message = getStatusMessage(page_name, action.payload);
      state.componentMessage = message;
      Snackbar.error(message);
    },

    // Assets Latest Polling
    getAssetsLatestPollingRequest: (state, action: AssetsRequestActionTypes) => {
      state.assetsLatestTimestamp = action.payload?.timestamp;
    },
    getAssetsLatestPollingResponse: (state, action) => {
      if (state.assetsLatestTimestamp === get(action, 'payload.res.timestamp', null)) {
        state.assetsLatest = get(action, 'payload.res.data', []);
        state.assetsCount = {
          connected: get(action, 'payload.res.connected_count', 0),
          disconnected: get(action, 'payload.res.disconnected_count', 0),
        };
      }
    },
    getAssetsLatestPollingFailed: (state, action) => {
      const message = getStatusMessage(page_name, action.payload);
      Snackbar.error(message);
    },

    getAssetsRequest: (state, action: AssetsRequestActionTypes) => {
      state.assetsLoading = true;
      state.assetsRequestFailed = false;
      state.assetsTimestamp = action.payload?.timestamp;
    },
    getAssetsResponse: (state, action) => {
      if (state.assetsTimestamp === get(action, 'payload.res.timestamp', null)) {
        state.assetsLoading = false;
        state.assets = get(action, 'payload.res.data', []);
        state.assetsTotalCount = get(action, 'payload.res.total_count', 0);
        state.assetsPageNumber = get(action, 'payload.res.page', 1);
      }
      // state.assetsLength = {
      //   connected_assets_count: get(action, 'payload.res.connected_count', 0),
      //   disconnected_assets_count: get(action, 'payload.res.disconnected_count', 0),
      // };
    },
    getAssetsFailed: (state, action) => {
      state.assetsLoading = false;
      state.assetsRequestFailed = true;
      const message = getStatusMessage(page_name, action.payload);
      state.componentMessage = message;
      Snackbar.error(message);
    },

    getAssetsOnboardingDataRequest: (state, action: AssetsOnboardingDataRequestActionTypes) => {
      state.assetsOnboardingLoading = true;
      state.assetsOnboardingRequestFailed = false;
    },
    getAssetsOnboardingDataResponse: (state, action) => {
      state.assetsOnboardingLoading = false;
      state.assetsOnboardingData = get(action, 'payload.res', {});
    },
    getAssetsOnboardingDataFailed: (state, action) => {
      state.assetsOnboardingLoading = false;
      state.assetsOnboardingRequestFailed = true;
      const message = getStatusMessage(page_name, action.payload);
      state.componentMessage = message;
      Snackbar.error(message);
    },

    // Gateways
    getGatewaysLengthRequest: (state, action: GatewaysLengthRequestActionTypes) => {
      state.gatewaysLengthLoading = true;
      state.gatewaysLengthRequestFailed = false;
    },
    getGatewaysLengthResponse: (state, action) => {
      state.gatewaysLengthLoading = false;
      state.gatewaysLength = get(action, 'payload.res', {});
    },
    getGatewaysLengthFailed: (state, action) => {
      state.gatewaysLengthLoading = false;
      state.gatewaysLengthRequestFailed = true;
      const message = getStatusMessage(page_name, action.payload);
      state.componentMessage = message;
      Snackbar.error(message);
    },

    // Gateways Latest
    getGatewaysLatestRequest: (state, action: GatewaysLatestRequestActionTypes) => {
      state.gatewaysLatestLoading = true;
      state.gatewaysLatestFailed = false;
      state.gatewaysLatestTimestamp = action.payload?.timestamp;
    },
    getGatewaysLatestResponse: (state, action) => {
      if (state.gatewaysLatestTimestamp === get(action, 'payload.res.timestamp', null)) {
        state.gatewaysLatestLoading = false;
        state.gatewaysLatest = get(action, 'payload.res.data', []);
        state.gatewaysCount = {
          connected: get(action, 'payload.res.connected_count', 0),
          disconnected: get(action, 'payload.res.disconnected_count', 0),
        };
      }
    },
    getGatewaysLatestFailed: (state, action) => {
      state.gatewaysLatestLoading = false;
      state.gatewaysLatestFailed = true;
      const message = getStatusMessage(page_name, action.payload);
      state.componentMessage = message;
      Snackbar.error(message);
    },
    // Gateways Latest Polling
    getGatewaysLatestPollingRequest: (state, action: GatewaysLatestRequestActionTypes) => {
      state.gatewaysLatestTimestamp = action.payload?.timestamp;
    },
    getGatewaysLatestPollingResponse: (state, action) => {
      if (state.gatewaysLatestTimestamp === get(action, 'payload.res.timestamp', null)) {
        state.gatewaysLatest = get(action, 'payload.res.data', []);
        state.gatewaysCount = {
          connected: get(action, 'payload.res.connected_count', 0),
          disconnected: get(action, 'payload.res.disconnected_count', 0),
        };
      }
    },
    getGatewaysLatestPollingFailed: (state, action) => {
      const message = getStatusMessage(page_name, action.payload);
      Snackbar.error(message);
    },

    getGatewaysRequest: (state, action: GatewaysLatestRequestActionTypes) => {
      state.gatewaysLoading = true;
      state.gatewaysRequestFailed = false;
      state.gatewaysTimestamp = action.payload?.timestamp;
    },
    getGatewaysResponse: (state, action) => {
      if (state.gatewaysTimestamp === get(action, 'payload.res.timestamp', null)) {
        state.gatewaysLoading = false;
        state.gateways = get(action, 'payload.res.data', []);
        state.gatewaysTotalCount = get(action, 'payload.res.total_count', 0);
        state.gatewaysPageNumber = get(action, 'payload.res.page', 1);
        // state.gatewaysLength = {
        //   connected_gateways_count: get(action, 'payload.res.connected_count', 0),
        //   disconnected_gateways_count: get(action, 'payload.res.disconnected_count', 0),
        // };
      }
    },
    getGatewaysFailed: (state, action) => {
      state.gatewaysLoading = false;
      state.gatewaysRequestFailed = true;
      const message = getStatusMessage(page_name, action.payload);
      state.componentMessage = message;
      Snackbar.error(message);
    },

    // MQ Broker
    getMQBrokerLengthRequest: (state, action: MQBrokerLengthRequestActionTypes) => {
      state.mqBrokerLengthLoading = true;
      state.mqBrokerLengthRequestFailed = false;
    },
    getMQBrokerLengthResponse: (state, action) => {
      state.mqBrokerLengthLoading = false;
      state.mqBrokerLength = get(action, 'payload.res', {});
    },
    getMQBrokerLengthFailed: (state, action) => {
      state.mqBrokerLengthLoading = false;
      state.mqBrokerLengthRequestFailed = true;
      const message = getStatusMessage(page_name, action.payload);
      state.componentMessage = message;
      Snackbar.error(message);
    },

    getMQBrokerRequest: (state, action: MQBrokerRequestActionTypes) => {
      state.mqBrokerLoading = true;
      state.assetsRequestFailed = false;
    },
    getMQBrokerResponse: (state, action) => {
      state.mqBrokerLoading = false;
      state.mqBroker = get(action, 'payload.res.data', []);
      state.mqBrokerTotalCount = get(action, 'payload.res.total_count', 0);
      state.mqBrokerPageNumber = get(action, 'payload.res.page', 1);
    },
    getMQBrokerFailed: (state, action) => {
      state.mqBrokerLoading = false;
      state.mqBrokerRequestFailed = true;
      const message = getStatusMessage(page_name, action.payload);
      state.componentMessage = message;
      Snackbar.error(message);
    },

    // Influx
    getInfluxLengthRequest: (state, action: InfluxLengthRequestActionTypes) => {
      state.influxLengthLoading = true;
      state.influxLengthRequestFailed = false;
    },
    getInfluxLengthResponse: (state, action) => {
      state.influxLengthLoading = false;
      state.influxLength = get(action, 'payload.res', { connected_influx_count: 0, disconnected_influx_count: 0 });
    },
    getInfluxLengthFailed: (state, action) => {
      state.influxLengthLoading = false;
      state.influxLengthRequestFailed = true;
      const message = getStatusMessage(page_name, action.payload);
      state.componentMessage = message;
      Snackbar.error(message);
    },

    getInfluxRequest: (state, action: MQBrokerRequestActionTypes) => {
      state.influxLoading = true;
      state.influxRequestFailed = false;
    },
    getInfluxResponse: (state, action) => {
      state.influxLoading = false;
      state.influx = get(action, 'payload.res.data', []);
      state.influxTotalCount = get(action, 'payload.res.total_count', 0);
      state.influxPageNumber = get(action, 'payload.res.page', 1);
    },
    getInfluxFailed: (state, action) => {
      state.influxLoading = false;
      state.influxRequestFailed = true;
      const message = getStatusMessage(page_name, action.payload);
      state.componentMessage = message;
      Snackbar.error(message);
    },

    // UI Server
    getUIServerLengthRequest: (state, action: UIServerLengthRequestActionTypes) => {
      state.uiServerLengthLoading = true;
      state.uiServerLengthRequestFailed = false;
    },
    getUIServerLengthResponse: (state, action) => {
      state.uiServerLengthLoading = false;
      state.uiServerLength = get(action, 'payload.res', {});
    },
    getUIServerLengthFailed: (state, action) => {
      state.uiServerLengthLoading = false;
      state.uiServerLengthRequestFailed = true;
      const message = getStatusMessage(page_name, action.payload);
      state.componentMessage = message;
      Snackbar.error(message);
    },

    getUIServerRequest: (state, action: MQBrokerRequestActionTypes) => {
      state.uiServerLoading = true;
      state.uiServerRequestFailed = false;
    },
    getUIServerResponse: (state, action) => {
      state.uiServerLoading = false;
      state.uiServer = get(action, 'payload.res.data', []);
      state.uiServerTotalCount = get(action, 'payload.res.total_count', 0);
      state.uiServerPageNumber = get(action, 'payload.res.page', 1);
    },
    getUIServerFailed: (state, action) => {
      state.uiServerLoading = false;
      state.uiServerRequestFailed = true;
      const message = getStatusMessage(page_name, action.payload);
      state.componentMessage = message;
      Snackbar.error(message);
    },

    // Backend Server
    getBackendServerLengthRequest: (state, action: BackendServerLengthRequestActionTypes) => {
      state.backendServerLengthLoading = true;
      state.backendServerLengthRequestFailed = false;
    },
    getBackendServerLengthResponse: (state, action) => {
      state.backendServerLengthLoading = false;
      state.backendServerLength = get(action, 'payload.res', {});
    },
    getBackendServerLengthFailed: (state, action) => {
      state.backendServerLengthLoading = false;
      state.backendServerLengthRequestFailed = true;
      const message = getStatusMessage(page_name, action.payload);
      state.componentMessage = message;
      Snackbar.error(message);
    },

    getBackendServerRequest: (state, action: MQBrokerRequestActionTypes) => {
      state.backendServerLoading = true;
      state.backendServerRequestFailed = false;
    },
    getBackendServerResponse: (state, action) => {
      state.backendServerLoading = false;
      state.backendServer = get(action, 'payload.res.data', []);
      state.backendServerTotalCount = get(action, 'payload.res.total_count', 0);
      state.backendServerPageNumber = get(action, 'payload.res.page', 1);
    },
    getBackendServerFailed: (state, action) => {
      state.backendServerLoading = false;
      state.backendServerRequestFailed = true;
      const message = getStatusMessage(page_name, action.payload);
      state.componentMessage = message;
      Snackbar.error(message);
    },

    // Notifications
    getNotificationsLatestRequest: (state, action: GatewaysLatestRequestActionTypes) => {
      state.notificationsLatestLoading = true;
      state.notificationsLatestFailed = false;
    },
    getNotificationsLatestResponse: (state, action) => {
      state.notificationsLatestLoading = false;
      state.notificationsLatest = get(action, 'payload.res.data', []);
    },
    getNotificationsLatestFailed: (state, action) => {
      state.notificationsLatestLoading = false;
      state.notificationsLatestFailed = true;
      const message = getStatusMessage(page_name, action.payload);
      state.componentMessage = message;
      Snackbar.error(message);
    },

    getNotificationsRequest: (state, action: NotificationsRequestActionTypes) => {
      state.notificationsLoading = true;
      state.notificationsRequestFailed = false;
    },
    getNotificationsResponse: (state, action) => {
      state.notificationsLoading = false;
      state.notifications = get(action, 'payload.res.data', []);
      state.notificationsTotalCount = get(action, 'payload.res.total_count', 0);
      state.notificationsPageNumber = get(action, 'payload.res.page', 1);
    },
    getNotificationsFailed: (state, action) => {
      state.notificationsLoading = false;
      state.notificationsRequestFailed = true;
      const message = getStatusMessage(page_name, action.payload);
      state.componentMessage = message;
      Snackbar.error(message);
    },

    // Telemetry
    getTelemetryIngestionDataRequest: (state, action: TelemtryIngestionDataRequestActionTypes) => {
      state.telemetryIngestionLoading = true;
      state.telemetryIngestionRequestFailed = false;
    },
    getTelemetryIngestionDataResponse: (state, action) => {
      state.telemetryIngestionLoading = false;
      state.telemetryIngestionData = get(action, 'payload.res', {});
    },
    getTelemetryIngestionDataFailed: (state, action) => {
      state.telemetryIngestionLoading = false;
      state.telemetryIngestionRequestFailed = true;
      const message = getStatusMessage(page_name, action.payload);
      state.componentMessage = message;
      Snackbar.error(message);
    },

    // Assets History
    getAssetsHistoryDataRequest: (state, action: AssetHistoryActionTypes) => {
      state.assetsHistoryLoading = true;
      state.assetsHistoryRequestFailed = false;
      state.assetsHistoryTimestamp = action?.payload?.timestamp;
    },
    getAssetsHistoryDataResponse: (state, action) => {
      // console.log(action.payload.res);
      if (state.assetsHistoryTimestamp === get(action, 'payload.res.timestamp', null)) {
        state.assetsHistoryLoading = false;
        state.assetsHistoryData = get(action, 'payload.res', {});
        state.assetHistoryTotalCount = get(action, 'payload.res.total_count', 0);
        state.assetHistoryPageNumber = get(action, 'payload.res.page', 1);
      }
    },
    getAssetsHistoryDataFailed: (state, action) => {
      state.assetsHistoryLoading = false;
      state.assetsHistoryRequestFailed = true;
      const message = getStatusMessage(page_name, action.payload);
      state.componentMessage = message;
      Snackbar.error(message);
    },

    // EMQX - Influx Count
    getEmqxInfluxCountRequest: (state, action: MQBrokerLengthRequestActionTypes) => {
      state.emqxInfluxCountLoading = true;
      state.emqxInfluxCountFailed = false;
    },
    getEmqxInfluxCountResponse: (state, action) => {
      state.emqxInfluxCountLoading = false;
      state.emqxInfluxCount = get(action, 'payload.res', {});
    },
    getEmqxInfluxCountFailed: (state, action) => {
      state.emqxInfluxCountLoading = false;
      state.emqxInfluxCountFailed = true;
      const message = getStatusMessage(page_name, action.payload);
      state.componentMessage = message;
      Snackbar.error(message);
    },

    // Assets Polling
    getAssetsPollingRequest: (state, action: AssetsRequestActionTypes) => {
      // state.assetsLoading = true;
      // state.assetsRequestFailed = false;
      state.assetsTimestamp = action.payload?.timestamp;
    },
    getAssetsPollingResponse: (state, action) => {
      if (state.assetsTimestamp === get(action, 'payload.res.timestamp', null)) {
        // state.assetsLoading = false;
        state.assets = get(action, 'payload.res.data', []);
        state.assetsTotalCount = get(action, 'payload.res.total_count', 0);
        state.assetsPageNumber = get(action, 'payload.res.page', 1);
        // state.assetsLength = {
        //   connected_assets_count: get(action, 'payload.res.connected_count', 0),
        //   disconnected_assets_count: get(action, 'payload.res.disconnected_count', 0),
        // };
      }
    },
    getAssetsPollingFailed: (state, action) => {
      // state.assetsLoading = false;
      // state.assetsRequestFailed = true;
      const message = getStatusMessage(page_name, action.payload);
      // state.componentMessage = message;
      Snackbar.error(message);
    },

    // Gateways Polling
    getGatewaysPollingRequest: (state, action: GatewaysLatestRequestActionTypes) => {
      // state.gatewaysLoading = true;
      // state.gatewaysRequestFailed = false;
      state.gatewaysTimestamp = action.payload?.timestamp;
    },
    getGatewaysPollingResponse: (state, action) => {
      if (state.gatewaysTimestamp === get(action, 'payload.res.timestamp', null)) {
        // state.gatewaysLoading = false;
        state.gateways = get(action, 'payload.res.data', []);
        state.gatewaysTotalCount = get(action, 'payload.res.total_count', 0);
        state.gatewaysPageNumber = get(action, 'payload.res.page', 1);
        state.gatewaysLength = {
          connected_gateways_count: get(action, 'payload.res.connected_count', 0),
          disconnected_gateways_count: get(action, 'payload.res.disconnected_count', 0),
        };
      }
    },
    getGatewaysPollingFailed: (state, action) => {
      // state.gatewaysLoading = false;
      // state.gatewaysRequestFailed = true;
      const message = getStatusMessage(page_name, action.payload);
      // state.componentMessage = message;
      Snackbar.error(message);
    },

    // Assets History
    getAssetsHistoryPollingDataRequest: (state, action: AssetHistoryActionTypes) => {
      // state.assetsHistoryLoading = true;
      // state.assetsHistoryRequestFailed = false;
      state.assetsHistoryTimestamp = action.payload?.timestamp;
    },
    getAssetsHistoryPollingDataResponse: (state, action) => {
      // state.assetsHistoryLoading = false;
      if (state.assetsHistoryTimestamp === get(action, 'payload.res.timestamp', null)) {
        state.assetsHistoryData = get(action, 'payload.res', {});
        state.assetHistoryTotalCount = get(action, 'payload.res.total_count', 0);
        state.assetHistoryPageNumber = get(action, 'payload.res.page', 1);
      }
    },
    getAssetsHistoryPollingDataFailed: (state, action) => {
      // state.assetsHistoryLoading = false;
      // state.assetsHistoryRequestFailed = true;
      const message = getStatusMessage(page_name, action.payload);
      // state.componentMessage = message;
      Snackbar.error(message);
    },
    resetAssetsTable: (state) => {
      state.assets = [];
      state.assetsTotalCount = 0;
      state.assetsPageNumber = 1;
    },
    resetGatewaysTable: (state) => {
      state.gateways = [];
      state.gatewaysTotalCount = 0;
      state.gatewaysPageNumber = 1;
    },
    resetAssetsHistoryTable: (state) => {
      state.assetsHistoryData = [];
      state.assetHistoryTotalCount = 0;
      state.assetHistoryPageNumber = 1;
    },
    resetMQTable: (state) => {
      state.mqBroker = [];
    },
    resetInfluxTable: (state) => {
      state.influx = [];
    },
    getAssetPropertiesDataRequest: (state, action: AssetHistoryActionTypes) => {
      state.assetsPropertiesLoading = true;
      state.assetsPropertiesRequestFailed = false;
      state.assetsPropertiesTimestamp = action?.payload?.timestamp;
    },
    getAssetPropertiesDataResponse: (state, action) => {
      // console.log(action.payload.res);
      // if (state.assetsHistoryTimestamp === get(action, 'payload.res.timestamp', null)) {
         state.assetsPropertiesRequestFailed = false;
        state.assetsPropertiesLoading = false;
        state.assetPropertiesData = get(action, 'payload.res.data', []);
        state.assetPropertiesTotalCount = get(action, 'payload.res.total_count', 0);
        state.assetPropertiesPageNumber = get(action, 'payload.res.page', 1);
      // }
    },
    getAssetPropertiesDataFailed: (state, action) => {
      state.assetsPropertiesLoading = false;
      state.assetsPropertiesRequestFailed = true;
      state.assetPropertiesData = []
      const message = getStatusMessage(page_name, action.payload);
      state.componentMessage = message;
      Snackbar.error(message);
    },
  },
});

export const connectivityDashboardActions = connectivityDashboardReducer.actions;
export default connectivityDashboardReducer;
